// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii } from '../../theme/tokens';
import { useApp, navigate } from '../../store/AppStore';
import { useResponsive } from '../../theme/responsive';
import { useAuth } from '../../store/AuthContext';
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';

// ─── Variable pill ────────────────────────────────────────────────────────────

function VarPill({ name, onPress }: { name: string; onPress?: () => void }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={styles.varPill}
    >
      <Text style={styles.varPillText}>{`{{${name}}}`}</Text>
    </TouchableOpacity>
  );
}

// ─── TemplateEditorScreen ─────────────────────────────────────────────────────

interface Props {
  slug?: string;
}

export function TemplateEditorScreen({ slug }: Props) {
  const { templates, updateTemplate, saveTemplate, restoreTemplate, sendTestEmail, toast } = useApp();
  const { user: authUser, org: authOrg } = useAuth();
  const { isMobile, pagePadX } = useResponsive();
  const t = (templates || []).find((x: any) => x.slug === slug);

  const [subject, setSubject] = useState(t?.subject || '');
  const [body, setBody] = useState(t?.body || '');

  useEffect(() => {
    if (t) { setSubject(t.subject); setBody(t.body); }
  }, [t?.subject, t?.body]);

  const variables: Array<[string, string]> = [
    ['ticket_number', 'TKT-1041'],
    ['ticket_subject', 'Billing discrepancy'],
    ['ticket_url', 'https://support.softknack.io/t/1041'],
    ['agent_name', authUser ? `${authUser.first_name} ${authUser.last_name}`.trim() : 'Agent Name'],
    ['org_name', authOrg?.name || 'Your Company'],
    ['comment_body', '(reply text)'],
    ['customer_name', 'Customer Name'],
    ['csat_url', 'https://support.softknack.io/c/abc123'],
    ['support_email', 'support@yourcompany.com'],
  ];

  if (!t) {
    return (
      <ScreenShell crumbs={[
        { label: 'Settings' },
        { label: 'Email Templates', to: '/admin/settings/email-templates' },
        { label: '?' },
      ]}>
        <View style={styles.notFound}>
          <Text style={styles.notFoundText}>Template not found.</Text>
        </View>
      </ScreenShell>
    );
  }

  const dirty = subject !== t.subject || body !== t.body;

  const insertVariable = (name: string) => {
    setBody(b => b + `{{${name}}}`);
    toast(`Inserted {{${name}}}.`, 'info', 'copy');
  };

  const handleSave = () => {
    updateTemplate?.(t.slug, { subject, body });
    saveTemplate?.(t.slug);
  };

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Email Templates', to: '/admin/settings/email-templates' },
      { label: t.name },
    ]}>
      <View style={styles.root}>
        {/* Page Head */}
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX, flexWrap: 'wrap' }]}>
          <View style={styles.pageHeadLeft}>
            <PrimaryButton
              title="Back to templates"
              size="sm"
              variant="ghost"
              leftIcon={<Icon name="ArrowLeft" size={13} color={Colors.ink2} />}
              onPress={() => navigate('/admin/settings/email-templates')}
            />
            <View style={{ marginTop: 8 }}>
              <View style={styles.titleRow}>
                <Text style={styles.pageTitle}>{t.name}</Text>
                <Text style={styles.pageTitleLocale}> · English (EN)</Text>
              </View>
              <Text style={styles.pageSub}>{t.desc}</Text>
            </View>
          </View>
          <View style={styles.pageHeadBtns}>
            <PrimaryButton
              title="Send test to me"
              size="md"
              variant="default"
              leftIcon={<Icon name="Send" size={12} color={Colors.ink} />}
              onPress={() => sendTestEmail?.(slug)}
            />
            <PrimaryButton
              title="Restore default"
              size="md"
              variant="ghost"
              leftIcon={<Icon name="Refresh" size={12} color={Colors.ink2} />}
              disabled={t.status !== 'customised'}
              onPress={() => restoreTemplate(t.slug)}
            />
            <PrimaryButton
              title="Save changes"
              size="md"
              variant="primary"
              leftIcon={<Icon name="Check" size={13} color={Colors.paper} />}
              disabled={!dirty}
              onPress={handleSave}
            />
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { paddingHorizontal: pagePadX }]}>
          <View style={[styles.layout, isMobile && styles.stack]}>
            {/* Left: Subject + Body */}
            <View style={{ flex: 1 }}>
              {/* Subject Card */}
              <View style={styles.card}>
                <View style={styles.cardRow}>
                  <Text style={styles.fieldLabel}>Subject</Text>
                  <TextInput
                    style={styles.input}
                    value={subject}
                    onChangeText={setSubject}
                    placeholder="Email subject"
                  />
                  <Text style={styles.fieldHint}>Shown in the customer's inbox. {'{{variables}}'} are highlighted.</Text>
                  {/* Subject preview */}
                  <View style={styles.subjectPreview}>
                    <Text style={styles.subjectPreviewLabel}>Preview: </Text>
                    <Text style={styles.subjectPreviewText}>{subject || '(empty)'}</Text>
                  </View>
                </View>
              </View>

              {/* Body Card */}
              <View style={styles.card}>
                <View style={styles.bodyCardHead}>
                  <Text style={styles.bodyCardTitle}>Email body</Text>
                  <Chip label="HTML" tone="default" size="sm" />
                </View>
                <View style={[styles.splitEditor, isMobile && styles.stack]}>
                  <TextInput
                    style={styles.bodyEditor}
                    value={body}
                    onChangeText={setBody}
                    multiline
                    textAlignVertical="top"
                    placeholder="<p>Email body HTML…</p>"
                  />
                  <View style={styles.bodyPreview}>
                    <Text style={styles.bodyPreviewText} numberOfLines={0}>{body}</Text>
                  </View>
                </View>
                <View style={styles.bodyCardFoot}>
                  <Text style={styles.footNote}>Edit on the left, see variables + tags highlighted on the right. Plaintext alternative is generated automatically.</Text>
                </View>
              </View>

              {/* Last saved note */}
              <Text style={styles.savedNote}>
                {t.editedAgo
                  ? `Last saved ${t.editedAgo} by ${t.editedBy}`
                  : 'Using the Softknack default — your edits will create a customised override.'}
              </Text>
            </View>

            {/* Right: Variables */}
            <View style={styles.rightPane}>
              <View style={styles.card}>
                <View style={styles.variablesHead}>
                  <Text style={styles.variablesTitle}>Variables</Text>
                  <Text style={styles.variablesSub}>Tap to insert · tap copy icon to copy.</Text>
                </View>
                <View style={styles.variablesList}>
                  {variables.map(([k, sample]) => (
                    <View key={k} style={styles.variableRow}>
                      <VarPill name={k} onPress={() => insertVariable(k)} />
                      <Text style={styles.sampleText} numberOfLines={1}>{sample}</Text>
                      <TouchableOpacity
                        onPress={() => toast(`Copied {{${k}}}`, 'info', 'copy')}
                        style={styles.copyBtn}
                      >
                        <Icon name="Copy" size={11} color={Colors.ink3} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              </View>

              <View style={styles.previewSendNote}>
                <View style={styles.previewSendHead}>
                  <Icon name="Bolt" size={12} color={Colors.ink2} />
                  <Text style={styles.previewSendTitle}>Preview send</Text>
                </View>
                <Text style={styles.previewSendDesc}>
                  "Send test to me" delivers a real email with sample values to <Text style={styles.mono}>{authUser?.email || 'your email'}</Text>.
                </Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  notFound: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  notFoundText: { fontSize: 14, color: Colors.ink3 },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageHeadLeft: { flex: 1 },
  titleRow: { flexDirection: 'row', alignItems: 'baseline' },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageTitleLocale: { fontSize: 16, fontWeight: FontWeight.regular, color: Colors.ink4 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  pageHeadBtns: { flexDirection: 'row', gap: 8, paddingTop: 8 },
  body: { flex: 1 },
  bodyContent: { padding: 20, maxWidth: 1080, alignSelf: 'center', width: '100%' as any },
  layout: { flexDirection: 'row', gap: 20 },
  stack: { flexDirection: 'column', gap: 16 },
  card: {
    backgroundColor: Colors.card,
    borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.line,
    marginBottom: 16, overflow: 'hidden',
  },
  cardRow: { padding: 16 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink, marginBottom: 4 },
  fieldHint: { fontSize: 12, color: Colors.ink3, marginTop: 4 },
  input: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 10, paddingVertical: 7,
    fontSize: 12.5, color: Colors.ink, backgroundColor: Colors.card,
  },
  subjectPreview: {
    marginTop: 10, padding: 8,
    backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2,
    flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 2,
  },
  subjectPreviewLabel: { fontSize: 12, color: Colors.ink4, marginRight: 4 },
  subjectPreviewText: { fontSize: 12, color: Colors.ink },
  bodyCardHead: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 14, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  bodyCardTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink2 },
  splitEditor: { flexDirection: 'row' },
  bodyEditor: {
    flex: 1, minHeight: 280, padding: 12,
    fontSize: 12, color: Colors.ink, backgroundColor: Colors.card,
    borderRightWidth: 1, borderRightColor: Colors.line,
    textAlignVertical: 'top',
  },
  bodyPreview: {
    flex: 1, minHeight: 280, padding: 12,
    backgroundColor: Colors.paper2,
  },
  bodyPreviewText: { fontSize: 12, color: Colors.ink, lineHeight: 20 },
  bodyCardFoot: {
    padding: '10px 14px' as any,
    paddingHorizontal: 14, paddingVertical: 10,
    borderTopWidth: 1, borderTopColor: Colors.line,
    backgroundColor: Colors.card,
  },
  footNote: { fontSize: 11.5, color: Colors.ink3 },
  savedNote: { fontSize: 12, color: Colors.ink3, marginTop: -8, marginBottom: 8 },
  rightPane: { width: 280 },
  variablesHead: {
    paddingHorizontal: 14, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  variablesTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  variablesSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },
  variablesList: { padding: 14, gap: 6 },
  variableRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  varPill: {
    paddingHorizontal: 8, paddingVertical: 3,
    backgroundColor: Colors.accentSoft,
    borderWidth: 1, borderColor: Colors.accentLine,
    borderRadius: Radii.r2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  varPillText: { fontSize: 12, color: Colors.accentInk, fontWeight: FontWeight.medium },
  sampleText: { flex: 1, fontSize: 11.5, color: Colors.ink3 },
  copyBtn: {
    width: 22, height: 22, justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  previewSendNote: {
    padding: 14,
    backgroundColor: Colors.card,
    borderWidth: 1, borderStyle: 'dashed' as any, borderColor: Colors.lineStrong,
    borderRadius: Radii.r3,
  },
  previewSendHead: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  previewSendTitle: { fontSize: 12, fontWeight: FontWeight.semibold, color: Colors.ink2 },
  previewSendDesc: { fontSize: 12, color: Colors.ink3, lineHeight: 18 },
  mono: { fontWeight: FontWeight.medium, color: Colors.ink2 },
});
